import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Panel, AdminHeader, Stat } from "@/pages/admin/_shared";
import { ChatCircle, Flag, Check, X, Warning, Heart, ShieldCheck } from "@phosphor-icons/react";

export default function AdminCommunity() {
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    const [p, s] = await Promise.all([
      api.get(`/admin/community/posts${filter ? `?status=${filter}` : ""}`),
      api.get("/admin/community/stats"),
    ]);
    setPosts(p.data); setStats(s.data);
  };
  useEffect(() => { load(); }, [filter]);

  const moderate = async (id, status) => {
    await api.patch(`/admin/community/posts/${id}?status=${status}`);
    load();
  };

  if (!stats) return <div className="p-10 text-ink-dim">LOADING COMMUNITY…</div>;

  return (
    <div className="p-6 lg:p-8" data-testid="admin-community">
      <AdminHeader section="COMMUNITY MODERATION" title={["KEEP IT ", "*CLEAN."]} />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-zinc-900 border border-zinc-900 mb-8">
        <Stat label="TOTAL POSTS" value={stats.total_posts} Icon={ChatCircle} testid="com-stat-total" />
        <Stat label="PENDING" value={stats.pending} Icon={Warning} color="text-brand-orange" testid="com-stat-pending" />
        <Stat label="APPROVED" value={stats.approved} Icon={ShieldCheck} color="text-brand-green" testid="com-stat-approved" />
        <Stat label="FLAGGED" value={stats.flagged} Icon={Flag} color="text-brand-red" testid="com-stat-flagged" />
        <Stat label="REPORTS" value={stats.reports_total} color="text-brand-red" testid="com-stat-reports" />
      </div>

      <Panel title="MODERATION QUEUE" tag="LIVE" testid="com-queue">
        <div className="flex gap-1 mb-5 flex-wrap">
          {["", "pending", "approved", "flagged", "removed"].map((s) => (
            <button
              key={s || "all"}
              onClick={() => setFilter(s)}
              data-testid={`com-filter-${s || "all"}`}
              className={`text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 border transition-colors ${
                filter === s ? "border-brand-red text-brand-red" : "border-zinc-800 text-ink-mute hover:border-zinc-600"
              }`}
            >
              {s || "ALL"}
            </button>
          ))}
        </div>
        {posts.length === 0 ? (
          <div className="text-xs text-ink-dim text-center py-10">No posts in this view.</div>
        ) : (
          <ul className="space-y-2">
            {posts.map((p) => (
              <li
                key={p.id}
                className={`border p-4 ${
                  p.status === "flagged" ? "border-brand-red/40 bg-brand-red/5"
                  : p.status === "removed" ? "border-zinc-900 bg-black/60 opacity-60"
                  : p.status === "approved" ? "border-brand-green/30 bg-brand-green/5"
                  : "border-zinc-900 bg-black/40"
                }`}
                data-testid={`com-post-${p.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 border border-zinc-800 flex items-center justify-center text-[10px] tracking-[0.2em] uppercase font-mono">
                    {p.author.slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-display font-bold uppercase tracking-tight text-sm">{p.author}</span>
                      <span className={`text-[9px] tracking-[0.3em] uppercase px-1.5 py-0.5 border ${
                        p.status === "approved" ? "border-brand-green text-brand-green"
                        : p.status === "flagged" ? "border-brand-red text-brand-red"
                        : p.status === "removed" ? "border-zinc-700 text-ink-dim"
                        : "border-brand-orange text-brand-orange"
                      }`}>
                        {p.status}
                      </span>
                      {p.reports > 0 && (
                        <span className="text-[9px] tracking-[0.3em] uppercase text-brand-red flex items-center gap-1">
                          <Flag size={10} weight="fill" /> {p.reports}
                        </span>
                      )}
                      <span className="text-[10px] tracking-[0.25em] uppercase text-ink-dim flex items-center gap-1 ml-auto">
                        <Heart size={10} weight="fill" /> {p.likes}
                      </span>
                    </div>
                    <p className="text-sm text-white mt-2 break-words">{p.content}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3 ml-12">
                  <button onClick={() => moderate(p.id, "approved")} data-testid={`com-approve-${p.id}`} className="text-[9px] tracking-[0.25em] uppercase border border-brand-green text-brand-green hover:bg-brand-green hover:text-black px-2.5 py-1 inline-flex items-center gap-1">
                    <Check size={10} weight="bold" /> APPROVE
                  </button>
                  <button onClick={() => moderate(p.id, "flagged")} data-testid={`com-flag-${p.id}`} className="text-[9px] tracking-[0.25em] uppercase border border-brand-orange text-brand-orange hover:bg-brand-orange hover:text-black px-2.5 py-1 inline-flex items-center gap-1">
                    <Flag size={10} weight="bold" /> FLAG
                  </button>
                  <button onClick={() => moderate(p.id, "removed")} data-testid={`com-remove-${p.id}`} className="text-[9px] tracking-[0.25em] uppercase border border-brand-red text-brand-red hover:bg-brand-red hover:text-black px-2.5 py-1 inline-flex items-center gap-1">
                    <X size={10} weight="bold" /> REMOVE
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
